import { cva, type VariantProps } from "class-variance-authority";
import { LoaderCircle } from "lucide-react";
import { Slot } from "radix-ui";
import type { ComponentProps } from "react";
import { cn } from "@/shared/lib/cn";

/**
 * Figma UI kit → Button (component set 463:2995). Variant mapping: docs/UI_CONVENTIONS.md «Button».
 * Primary stays on brand-600: white text on brand-500 is below WCAG AA (Figma draws brand-500).
 */
const buttonVariants = cva(
  "inline-flex shrink-0 items-center justify-center gap-2 font-medium whitespace-nowrap transition-colors disabled:pointer-events-none disabled:opacity-50 [&_svg]:shrink-0",
  {
    variants: {
      variant: {
        primary: "bg-brand-600 text-white hover:bg-brand-700 active:bg-brand-800",
        secondary: "bg-canvas text-ink hover:bg-line active:bg-line-strong",
        /** Figma Outline 1. */
        outline:
          "border border-brand-600 bg-surface text-brand-700 hover:bg-brand-50 active:bg-brand-100",
        /** Figma Outline 2. */
        "outline-neutral": "border border-line bg-surface text-ink hover:bg-canvas active:bg-line",
        /** Figma Tertiary 2. */
        ghost: "text-ink hover:bg-canvas active:bg-line",
        /** Figma Tertiary 1. */
        "ghost-brand": "text-brand-700 hover:bg-brand-50 active:bg-brand-100",
        /** Figma Destructive Primary. */
        destructive: "bg-danger text-white hover:bg-danger/90 active:bg-danger/80",
        /** Figma Destructive Secondary. */
        "destructive-soft": "bg-danger/10 text-danger hover:bg-danger/20 active:bg-danger/30",
        link: "text-brand-700 underline-offset-4 hover:underline",
      },
      size: {
        xs: "h-7 rounded-md px-2 text-sm [&_svg:not([class*='size-'])]:size-4",
        sm: "h-9 rounded-lg2 px-3 text-sm [&_svg:not([class*='size-'])]:size-4",
        md: "h-11 rounded-xl px-5 text-sm [&_svg:not([class*='size-'])]:size-5",
        lg: "h-12 rounded-xl px-6 text-base [&_svg:not([class*='size-'])]:size-5",
      },
      /** Icon only (Figma «Float»): a square of the button's height; give it an `aria-label`. */
      square: { true: "px-0" },
      /** Figma draws Loading fully opaque, unlike Disabled. */
      loading: { true: "cursor-progress" },
    },
    compoundVariants: [
      { square: true, size: "xs", className: "size-7" },
      { square: true, size: "sm", className: "size-9" },
      { square: true, size: "md", className: "size-11" },
      { square: true, size: "lg", className: "size-12" },
    ],
    defaultVariants: { variant: "primary", size: "md" },
  },
);

type ButtonProps = ComponentProps<"button"> &
  VariantProps<typeof buttonVariants> & {
    /** Render the child element (e.g. a router Link) with button styles. `loading` is ignored then. */
    asChild?: boolean;
  };

export function Button({
  className,
  variant,
  size,
  square,
  loading = false,
  asChild = false,
  disabled,
  onClick,
  children,
  ...props
}: ButtonProps) {
  const classes = cn(buttonVariants({ variant, size, square, loading }), className);
  if (asChild) {
    return (
      <Slot.Root className={classes} onClick={onClick} {...props}>
        {children}
      </Slot.Root>
    );
  }
  // While loading the button stays focusable (native `disabled` would drop keyboard focus to <body>) but ignores
  // clicks and Enter, including form submission.
  return (
    <button
      className={classes}
      disabled={disabled}
      aria-disabled={loading === true ? true : undefined}
      aria-busy={loading === true ? true : undefined}
      onClick={(event) => {
        if (loading === true) {
          event.preventDefault();
          return;
        }
        onClick?.(event);
      }}
      {...props}
    >
      {loading && <LoaderCircle className="animate-spin" aria-hidden />}
      {children}
    </button>
  );
}
